import { useContext, useState, type JSX } from 'react';
import { Box, ButtonBase, Divider, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { MountPointContext } from '@repo/common-utils/mountPoint';
import { useAtom } from 'ximple';
import { formatCurrencyNoDecimalsIfPossible, useLocale } from '@repo/i18n';
import { currencyAtom } from '@repo/widget-utils/currencyAtom';
import {
    nativeMemberCoverageAtom,
    setMemberCardLineExcluded,
    setMemberCardLineOverride,
} from 'src/state/native-membership/nativeMemberCoverageAtom';
import { findNativeCoverageLine } from './nativeCoverage';
import MemberCardLinePicker from './MemberCardLinePicker';

type MemberCardCoveragePillProps = {
    lineKey: string;
    /** Total ticket quantity for the category — enables the "Split into groups" affordance. */
    quantity?: number;
    /** Invoked from the "Split into groups" menu item (only offered when there's a real choice). */
    onSplit?: () => void;
};

// Per-line member-card coverage pill + contextual menu (the widget twin of POS's CartLineCoverageBadge):
// shows which card covers a line, and lets the member override the card, pay with money, keep auto, or
// split the category. Renders nothing for lines no card can ever apply to.
export default function MemberCardCoveragePill({
    lineKey,
    quantity,
    onSplit,
}: MemberCardCoveragePillProps): JSX.Element | null {
    const { t, locale } = useLocale();
    const theme = useTheme();
    const [currency] = useAtom(currencyAtom);
    const [{ result, lineOverrides, excludedLineKeys, lineGroups }] =
        useAtom(nativeMemberCoverageAtom);
    const mountPoint = useContext(MountPointContext);

    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);

    const line = findNativeCoverageLine(result, lineKey);
    const isExcluded = excludedLineKeys.includes(lineKey);
    const hasOverride = lineKey in lineOverrides;

    // A line no card can apply to (none eligible) gets no pill — unless the member explicitly excluded it.
    if (!isExcluded && (!line || (!line.eligible && line.eligible_card_ids.length === 0))) {
        return null;
    }

    const activeCards = result?.member_card?.active_cards ?? [];
    const pickerCards = activeCards.filter((card) =>
        (line?.eligible_card_ids ?? []).includes(card.id),
    );
    const selectedCardId = line?.eligible ? line.selected_card_id : null;
    const breakdown = line?.eligible ? (line.coverage_breakdown ?? []) : [];
    const isSplit = breakdown.length > 1;
    const cardName = (id: string) => activeCards.find((card) => card.id === id)?.product_name ?? id;
    const money = (value: number) => formatCurrencyNoDecimalsIfPossible(locale, currency, value);

    // A tinted, chevroned pill so it reads as a tappable control: green = covered, amber = the picked
    // card fell back, muted = excluded / not applicable. A dot marks a member-touched line.
    let label: string;
    let tint: string;
    let showDot = false;
    if (isExcluded) {
        label = t.pay_with_money_instead;
        tint = theme.palette.text.disabled;
    } else if (line?.eligible) {
        const lead = isSplit
            ? `${breakdown.length} ${t.cards}`
            : (line.selected_card_product_name ?? cardName(line.selected_card_id ?? ''));
        label = `${lead} · −${money(line.discount)}`;
        const isFallback = line.selection_source === 'override_fallback';
        tint = isFallback ? theme.palette.warning.main : theme.palette.success.main;
        showDot = line.selection_source !== 'auto';
    } else {
        label = t.member_card_not_applicable;
        tint = theme.palette.text.disabled;
    }

    const closeMenu = () => setMenuAnchor(null);
    const keepAuto = () => {
        if (isExcluded) setMemberCardLineExcluded(lineKey, false);
        setMemberCardLineOverride(lineKey, null);
        closeMenu();
    };

    return (
        <Box sx={{ mt: 0.5 }}>
            <ButtonBase
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                aria-label={label}
                aria-haspopup="true"
                aria-expanded={Boolean(menuAnchor)}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    minHeight: 40,
                    pl: 1.5,
                    pr: 1,
                    py: 0.5,
                    borderRadius: '999px',
                    color: tint,
                    bgcolor: alpha(tint, 0.12),
                    border: `1px solid ${alpha(tint, 0.32)}`,
                }}
            >
                {showDot && (
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: theme.palette.primary.main,
                            flexShrink: 0,
                        }}
                    />
                )}
                <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ color: 'inherit', lineHeight: 1.2 }}
                >
                    {label}
                </Typography>
                <Box
                    component="span"
                    sx={{
                        fontSize: 11,
                        opacity: 0.65,
                        lineHeight: 1,
                        transition: 'transform 150ms ease',
                        transform: menuAnchor ? 'rotate(180deg)' : 'none',
                    }}
                >
                    ▾
                </Box>
            </ButtonBase>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={closeMenu}
                container={mountPoint.popover}
            >
                {isSplit && [
                    <Box key="breakdown" sx={{ px: 2, py: 1, maxWidth: 280 }}>
                        {breakdown.map((segment) => (
                            <Box
                                key={segment.card_id}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    mt: 0.5,
                                }}
                            >
                                <Typography variant="caption" noWrap>
                                    {cardName(segment.card_id)}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ color: theme.palette.text.secondary }}
                                >
                                    {segment.units} {t.tickets}
                                </Typography>
                            </Box>
                        ))}
                    </Box>,
                    <Divider key="breakdown-divider" sx={{ my: 0.5 }} />,
                ]}
                {pickerCards.length > 1 && (
                    <MenuItem
                        onClick={() => {
                            setPickerAnchor(menuAnchor);
                            closeMenu();
                        }}
                    >
                        {t.choose_another_card}
                    </MenuItem>
                )}
                {!isExcluded && (
                    <MenuItem
                        onClick={() => {
                            setMemberCardLineExcluded(lineKey, true);
                            closeMenu();
                        }}
                    >
                        {t.pay_with_money_instead}
                    </MenuItem>
                )}
                {(isExcluded || hasOverride) && (
                    <MenuItem onClick={keepAuto}>{t.use_auto_selection}</MenuItem>
                )}
                {onSplit &&
                    (quantity ?? 0) > 1 &&
                    pickerCards.length > 1 &&
                    !lineGroups[lineKey] && (
                        <MenuItem
                            onClick={() => {
                                onSplit();
                                closeMenu();
                            }}
                        >
                            {t.split_into_groups}
                        </MenuItem>
                    )}
            </Menu>

            <MemberCardLinePicker
                anchorEl={pickerAnchor}
                open={Boolean(pickerAnchor)}
                onClose={() => setPickerAnchor(null)}
                cards={pickerCards}
                selectedCardId={selectedCardId}
                onPick={(cardId) => setMemberCardLineOverride(lineKey, cardId)}
                onUseAuto={() => setMemberCardLineOverride(lineKey, null)}
            />
        </Box>
    );
}
